import { Fragment, useEffect, useState, type CSSProperties } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { warehouseApi, type StatusFilter, type StockPage, type StockRow } from "../../api/warehouse";
import { useAuth } from "../../auth/AuthContext";
import { TableSkeleton } from "../../components/Loading";
import { LotExpiry, daniDo } from "../../components/LotExpiry";
import { LotQualityBadge } from "../../components/LotQualityBadge";
import { formatDateTime, formatNumber } from "../../lib/format";
import { LotHolds } from "../warehouse/LotReservations";
import { chip, chips, pane, sectionTitle, tile, tileK, tileV } from "./sheetStyles";

/** Koliko LOT-ova se vidi pre „Prikaži sve“: oni koji prvi ističu, jer ta roba treba prva da izađe. */
const PRVIH = 8;
/** Redosled dugmadi statusa: prvo ono što sme kupcu. */
const STATUSI: StatusFilter[] = ["CONFORMING", "DOMESTIC_ONLY", "OPEN", "NONCONFORMING"];

export type ProductStockState = { data: StockPage | null; failed: boolean; reload: () => void };

/**
 * Stanje artikala jednog proizvoda iz Magacina: samog artikla, ili svih pakovanja kataloškog proizvoda. Čita se samo
 * kad sme (`enabled`): kupac Magacin ne vidi, pa ga strana proizvoda za njega ni ne pita.
 */
export function useProductStock(codes: string[], enabled: boolean): ProductStockState {
  const [data, setData] = useState<StockPage | null>(null);
  const [failed, setFailed] = useState(false);
  const [osvezi, setOsvezi] = useState(0);
  const kljuc = codes.join(",");

  useEffect(() => {
    const artikli = kljuc.split(",").filter(Boolean);
    if (!enabled || artikli.length === 0) return;
    let vazi = true;
    setFailed(false);
    warehouseApi
      .stock({ articles: artikli, size: 1000 })
      .then((d) => {
        if (vazi) setData(d);
      })
      .catch(() => {
        if (vazi) {
          setData(null);
          setFailed(true);
        }
      });
    return () => {
      vazi = false;
    };
  }, [kljuc, enabled, osvezi]);

  return { data, failed, reload: () => setOsvezi((n) => n + 1) };
}

/** Jedan LOT artikla, gde god mu palete stoje: pozicije po poljima sabrane u jedan red. */
type Lot = {
  red: StockRow;
  /** Skladište → njegova polja sa ovim LOT-om; skladište bez polja (diskont) ima prazan spisak. */
  mesta: Map<string, string[]>;
  proizvodni: boolean;
  stanje: number;
  rezervisano: number;
  slobodno: number;
};

/**
 * Stanje u magacinu na strani proizvoda, za zaposlene: koliko artikla ima, koliko je rezervisano, koliko sme da se
 * proda i koliko čeka ocenu kvaliteta, pa LOT-ovi po roku (prvo onaj koji pre ističe) sa statusom, slobodnim i za koga
 * se drže. Isti brojevi i isti prikaz roka, statusa i rezervacija kao u Magacinu; strana samo gleda jedan artikal.
 *
 * Kataloški proizvod ima više pakovanja (artikala). Stanje prati izabrano pakovanje, a čipovi iznad kažu koja pakovanja
 * imaju robu i biraju ih jednim dodirom.
 */
export function ProductStock({
  state,
  article,
  packagings,
  onPick,
}: {
  state: ProductStockState;
  /** Šifra artikla koji se gleda: izabrano pakovanje ili sam proizvod. */
  article: string;
  /** Pakovanja koja strana nudi u izboru; čip vodi samo na njih. */
  packagings?: string[];
  onPick?: (code: string) => void;
}) {
  const { t, i18n } = useTranslation();
  const lang = i18n.resolvedLanguage;
  const { hasPermission } = useAuth();
  const mozeSledljivost = hasPermission("section:traceability:read");
  const mozePorudzbine = hasPermission("section:orders:read");
  const [status, setStatus] = useState<StatusFilter | null>(null);
  const [sve, setSve] = useState(false);
  const { data, failed, reload } = state;

  const kolicina = (q: number, unit: string | null | undefined) =>
    `${formatNumber(q, lang, { maximumFractionDigits: 3 })} ${unit ?? ""}`.trim();

  const naslov = (
    <h2 style={naslovReda}>
      <span>{t("catalog.pdp.stock.title", "Stanje u magacinu")}</span>
      <Link to={`/warehouse?q=${encodeURIComponent(article)}`} style={naslovVeza}>
        {t("catalog.pdp.stock.openWarehouse", "Otvori u Magacinu")}
      </Link>
    </h2>
  );

  if (failed) {
    return (
      <section style={odeljak}>
        {naslov}
        <div style={{ ...pane, color: "var(--ink-3)" }}>{t("warehouse.loadFailed", "Stanje nije moglo da se učita.")}</div>
      </section>
    );
  }
  if (!data) {
    return (
      <section style={odeljak}>
        {naslov}
        <TableSkeleton rows={3} cols={4} label={t("warehouse.loading", "Čitam stanje…")} />
      </section>
    );
  }

  const lotovi = poLotu(data.rows.filter((r) => r.erpProductId === article));
  const unit = lotovi[0]?.red.unit ?? data.rows[0]?.unit ?? null;
  // Pakovanja redom kojim ih nudi izbor iznad; ono kog nema u izboru ide na kraj.
  const redIzbora = (code: string) => {
    const i = packagings?.indexOf(code) ?? -1;
    return i < 0 ? Number.MAX_SAFE_INTEGER : i;
  };
  const pakovanja = poArtiklu(data.rows).sort((a, b) => redIzbora(a.code) - redIzbora(b.code));
  const vise = pakovanja.length > 1 || (pakovanja.length === 1 && pakovanja[0].code !== article);
  const razlika = razlikeNaziva(pakovanja.map((p) => p.name ?? p.code));

  // Pločice: ono što sme kupcu i čemu rok nije prošao, i ono što kvalitet tek treba da oceni.
  const sme = lotovi.filter(
    (l) =>
      (l.red.qualityStatus === "CONFORMING" || l.red.qualityStatus === "DOMESTIC_ONLY") &&
      (!l.red.expiresOn || daniDo(l.red.expiresOn) >= 0),
  );
  const zaProdaju = sme.reduce((n, l) => n + Math.max(0, l.slobodno), 0);
  const odTogaSs = sme
    .filter((l) => l.red.qualityStatus === "DOMESTIC_ONLY")
    .reduce((n, l) => n + Math.max(0, l.slobodno), 0);
  const neocenjeni = lotovi.filter((l) => !l.red.qualityStatus);
  const broj = (s: StatusFilter) =>
    lotovi.filter((l) => (s === "OPEN" ? !l.red.qualityStatus : l.red.qualityStatus === s)).length;
  const statusi = STATUSI.filter((s) => broj(s) > 0);
  const izabrani = status == null ? lotovi : lotovi.filter((l) => (status === "OPEN" ? !l.red.qualityStatus : l.red.qualityStatus === status));
  const prikazani = sve ? izabrani : izabrani.slice(0, PRVIH);

  return (
    <section style={odeljak} aria-label={t("catalog.pdp.stock.title", "Stanje u magacinu")}>
      {naslov}
      <div style={podnaslov}>
        {t("warehouse.code", "šifra {{code}}", { code: article })}
        {" · "}
        {data.syncedAt
          ? t("catalog.pdp.stock.readAt", "stanje iz ERP-a {{at}}", { at: formatDateTime(data.syncedAt, lang) })
          : t("warehouse.notSynced", "Stanje iz ERP-a još nije pročitano.")}
      </div>
      {data.failedAt && (
        <span className="tag warn" style={{ whiteSpace: "normal" }}>
          {t("warehouse.failed", "Poslednje čitanje ERP-a nije uspelo ({{at}}), pa je prikazano starije stanje.", {
            at: formatDateTime(data.failedAt, lang),
          })}
        </span>
      )}

      {vise && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
          <div style={tileK}>
            {t("catalog.pdp.stock.packagings", "Pakovanja na stanju")}{" "}
            <span style={zbirPakovanja}>
              ·{" "}
              {t("catalog.pdp.stock.total", "ukupno {{qty}}", {
                qty: kolicina(pakovanja.reduce((n, p) => n + p.stanje, 0), pakovanja[0]?.unit),
              })}
            </span>
          </div>
          <div style={chips}>
            {pakovanja.map((p) => {
              const tekst = `${razlika(p.name ?? p.code)} · ${kolicina(p.stanje, p.unit)}`;
              const opis = `${p.name ?? ""} · ${p.code}`;
              const izabran = p.code === article;
              return onPick && packagings?.includes(p.code) ? (
                <button
                  key={p.code}
                  type="button"
                  aria-pressed={izabran}
                  title={opis}
                  onClick={() => onPick(p.code)}
                  style={izabran ? cipIzabran : cipDugme}
                >
                  {tekst}
                </button>
              ) : (
                <span key={p.code} title={opis} style={izabran ? cipIzabran : chip}>
                  {tekst}
                </span>
              );
            })}
          </div>
        </div>
      )}

      {lotovi.length === 0 ? (
        <div style={{ ...pane, color: "var(--ink-3)" }}>
          {data.syncedAt
            ? vise
              ? t("catalog.pdp.stock.noneOfPackaging", "Ovog pakovanja nema na stanju.")
              : t("catalog.pdp.stock.none", "Ovog artikla nema na stanju.")
            : t("warehouse.notSynced", "Stanje iz ERP-a još nije pročitano.")}
        </div>
      ) : (
        <>
          <div style={plocice}>
            <div style={tile}>
              <div style={tileK}>{t("catalog.pdp.stock.onHand", "Na stanju")}</div>
              <div style={vrednost}>{kolicina(lotovi.reduce((n, l) => n + l.stanje, 0), unit)}</div>
              <div style={ispod}>{t("catalog.pdp.stock.lots", "LOT-ova: {{n}}", { n: lotovi.length })}</div>
            </div>
            <div style={tile}>
              <div style={tileK}>{t("catalog.pdp.stock.reserved", "Rezervisano")}</div>
              <div style={vrednost}>{kolicina(lotovi.reduce((n, l) => n + l.rezervisano, 0), unit)}</div>
              <div style={ispod}>
                {t("catalog.pdp.stock.lots", "LOT-ova: {{n}}", { n: lotovi.filter((l) => l.rezervisano > 0).length })}
              </div>
            </div>
            <div
              style={tile}
              title={t(
                "catalog.pdp.stock.sellableHint",
                "Usaglašeni i SS LOT-ovi kojima rok nije prošao, bez rezervisanog. SS je samo za kupce u Srbiji.",
              )}
            >
              <div style={tileK}>{t("catalog.pdp.stock.sellable", "Slobodno za prodaju")}</div>
              <div style={{ ...vrednost, color: zaProdaju > 0 ? "var(--ok)" : vrednost.color }}>{kolicina(zaProdaju, unit)}</div>
              <div style={ispod}>
                {t("catalog.pdp.stock.lots", "LOT-ova: {{n}}", { n: sme.length })}
                {odTogaSs > 0 &&
                  ` · ${t("catalog.pdp.stock.ofWhichSs", "od toga SS {{qty}}", { qty: kolicina(odTogaSs, unit) })}`}
              </div>
            </div>
            <div style={tile}>
              <div style={tileK}>{t("catalog.pdp.stock.awaiting", "Čeka ocenu kvaliteta")}</div>
              <div style={vrednost}>{kolicina(neocenjeni.reduce((n, l) => n + l.stanje, 0), unit)}</div>
              <div style={ispod}>{t("catalog.pdp.stock.lots", "LOT-ova: {{n}}", { n: neocenjeni.length })}</div>
            </div>
          </div>

          {statusi.length > 1 && (
            <div className="seg" role="group" aria-label={t("warehouse.statusFilter", "Status kvaliteta")} style={{ alignSelf: "flex-start" }}>
              <button type="button" aria-pressed={status == null} onClick={() => setStatus(null)}>
                {t("catalog.pdp.stock.all", "Svi")} ({lotovi.length})
              </button>
              {statusi.map((s) => (
                <button key={s} type="button" aria-pressed={status === s} onClick={() => setStatus(s)}>
                  {filterTekst(t, s)} ({broj(s)})
                </button>
              ))}
            </div>
          )}

          {/* Kartica sama klizi vodoravno kad zatreba, pa tabela na telefonu ostaje tabela preko cele širine (opšte
              pravilo za telefon od svake pravi blok, koji se skupi na širinu sadržaja). */}
          <div className="card flat" style={{ padding: 0, overflowX: "auto" }}>
            <table className="tbl" style={{ fontSize: 12.5, display: "table" }}>
              <thead>
                <tr>
                  <th style={celija}>{t("warehouse.col.lot", "LOT")}</th>
                  <th style={celija}>{t("warehouse.col.expires", "Ističe")}</th>
                  <th style={celija}>{t("warehouse.col.quality", "Kvalitet")}</th>
                  <th className="num" style={celija}>{t("catalog.pdp.stock.col.free", "Slobodno")}</th>
                </tr>
              </thead>
              <tbody>
                {prikazani.map((l) => {
                  const drzi = l.red.reservations.length > 0;
                  // Red sa rezervacijama pripada svom LOT-u, pa između njih nema linije.
                  const c = drzi ? { ...celija, borderBottom: 0 } : celija;
                  return (
                    <Fragment key={l.red.erpLotId}>
                      <tr>
                        <td style={c}>
                          <span className="mono" style={{ color: "var(--ink)" }}>
                            {mozeSledljivost && l.red.goodsLotId ? (
                              <Link to={`/traceability?lot=${l.red.goodsLotId}`}>{l.red.lot}</Link>
                            ) : (
                              l.red.lot
                            )}
                          </span>
                          <div className="muted" style={sitno}>
                            {mestoTekst(l.mesta)}{" "}
                            {l.proizvodni && <span className="tag info">{t("warehouse.productionTag", "proizvodni")}</span>}
                          </div>
                        </td>
                        <td style={c}>
                          <LotExpiry iso={l.red.expiresOn} lang={lang} />
                        </td>
                        <td style={c}>
                          <LotQualityBadge status={l.red.qualityStatus} note={l.red.qualityNote} />
                          {l.red.qualityNote && (
                            <div className="muted" style={napomena} title={l.red.qualityNote}>
                              {l.red.qualityNote}
                            </div>
                          )}
                        </td>
                        <td className="num" style={{ ...c, whiteSpace: "nowrap" }}>
                          <b style={l.slobodno < 0 ? { color: "var(--err)" } : undefined}>{kolicina(l.slobodno, l.red.unit)}</b>
                          {l.rezervisano > 0 && (
                            <div className="muted" style={sitno}>
                              {t("catalog.pdp.stock.ofStock", "od {{qty}}", { qty: kolicina(l.stanje, l.red.unit) })}
                            </div>
                          )}
                        </td>
                      </tr>
                      {drzi && (
                        <tr>
                          <td colSpan={4} style={{ ...celija, paddingTop: 0, fontSize: 12 }}>
                            <LotHolds red={l.red} lang={lang} mozePorudzbine={mozePorudzbine} onChanged={reload} />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
          {izabrani.length > PRVIH && (
            <button type="button" className="btn ghost sm" style={{ alignSelf: "flex-start" }} onClick={() => setSve(!sve)}>
              {sve
                ? t("catalog.pdp.stock.showLess", "Prikaži manje")
                : t("catalog.pdp.stock.showAll", "Prikaži sve LOT-ove ({{n}})", { n: izabrani.length })}
            </button>
          )}
        </>
      )}
    </section>
  );
}

type T = ReturnType<typeof useTranslation>["t"];

function filterTekst(t: T, s: StatusFilter): string {
  switch (s) {
    case "OPEN":
      return t("warehouse.filter.OPEN", "Neobrađeni");
    case "CONFORMING":
      return t("warehouse.filter.CONFORMING", "Usaglašeni");
    case "DOMESTIC_ONLY":
      return t("warehouse.filter.DOMESTIC_ONLY", "SS");
    case "NONCONFORMING":
      return t("warehouse.filter.NONCONFORMING", "Neusaglašeni");
  }
}

/**
 * Pozicije sabrane po LOT-u, redom kojim ih server šalje (po roku). Rezervisano i slobodno su LOT-ovi, ne pozicije,
 * pa se uzimaju jednom.
 */
function poLotu(rows: StockRow[]): Lot[] {
  const lotovi = new Map<string, Lot>();
  for (const r of rows) {
    const l = lotovi.get(r.erpLotId);
    if (l) {
      dodajMesto(l.mesta, r);
      l.proizvodni ||= r.production;
      l.stanje += r.quantity;
      continue;
    }
    lotovi.set(r.erpLotId, {
      red: r,
      mesta: dodajMesto(new Map(), r),
      proizvodni: r.production,
      stanje: r.quantity,
      rezervisano: r.lotReserved ?? 0,
      slobodno: 0,
    });
  }
  for (const l of lotovi.values()) {
    l.stanje = l.red.lotStock ?? l.stanje;
    l.slobodno = l.red.lotFree ?? l.stanje - l.rezervisano;
  }
  return [...lotovi.values()];
}

function dodajMesto(mesta: Map<string, string[]>, r: StockRow): Map<string, string[]> {
  const skladiste = r.warehouseName || String(r.warehouseId);
  const polja = mesta.get(skladiste) ?? [];
  if (r.fieldName && !polja.includes(r.fieldName)) polja.push(r.fieldName);
  return mesta.set(skladiste, polja);
}

/** Gde LOT stoji: „Bioprotein · 11-0, 11-1“, a skladište bez polja samo imenom („Diskont Paracin“). */
function mestoTekst(mesta: Map<string, string[]>): string {
  return [...mesta].map(([skladiste, polja]) => (polja.length ? `${skladiste} · ${polja.join(", ")}` : skladiste)).join("; ");
}

/** Artikli (pakovanja) sa robom, sa zbirom, onim redom kojim ih šalje server. */
function poArtiklu(rows: StockRow[]) {
  const artikli = new Map<string, { code: string; name: string | null; unit: string | null; stanje: number }>();
  for (const r of rows) {
    const a = artikli.get(r.erpProductId);
    if (a) a.stanje += r.quantity;
    else artikli.set(r.erpProductId, { code: r.erpProductId, name: r.productName, unit: r.unit, stanje: r.quantity });
  }
  return [...artikli.values()];
}

/**
 * Ono po čemu se pakovanja razlikuju: naziv bez početka zajedničkog svim pakovanjima, odsečenog na celu reč
 * („TEXPRO M … ljuspice (1-4 mm) (15/1)“ → „(1-4 mm) (15/1)“). Kad je zajednički ceo naziv, ostaje ceo.
 */
function razlikeNaziva(nazivi: string[]): (naziv: string) => string {
  if (nazivi.length < 2) return (n) => n;
  let pocetak = nazivi[0];
  for (const n of nazivi) {
    let i = 0;
    while (i < pocetak.length && i < n.length && pocetak[i] === n[i]) i++;
    pocetak = pocetak.slice(0, i);
  }
  const rez = pocetak.lastIndexOf(" ") + 1;
  return (n) => n.slice(rez).trim() || n;
}

const odeljak: CSSProperties = { display: "flex", flexDirection: "column", gap: 10 };
const naslovReda: CSSProperties = {
  ...sectionTitle,
  margin: "10px 0 0",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "baseline",
  gap: 12,
};
const naslovVeza: CSSProperties = {
  fontSize: 12,
  fontWeight: 500,
  letterSpacing: 0,
  textTransform: "none",
  whiteSpace: "nowrap",
};
const podnaslov: CSSProperties = { fontSize: 12, color: "var(--ink-3)", marginTop: -4 };
const plocice: CSSProperties = { display: "grid", gridTemplateColumns: "repeat(2, minmax(0, 1fr))", gap: 8 };
const vrednost: CSSProperties = { ...tileV, fontSize: 15, fontVariantNumeric: "tabular-nums", color: "var(--ink)" };
const ispod: CSSProperties = { fontSize: 11, color: "var(--ink-3)", marginTop: 2 };
const zbirPakovanja: CSSProperties = { textTransform: "none", letterSpacing: 0, fontSize: 11, color: "var(--ink-3)" };
const celija: CSSProperties = { padding: "9px 10px" };
const sitno: CSSProperties = { fontSize: 11.5, marginTop: 2 };
const napomena: CSSProperties = {
  ...sitno,
  display: "-webkit-box",
  WebkitLineClamp: 2,
  WebkitBoxOrient: "vertical",
  overflow: "hidden",
  maxWidth: 160,
};
const cipDugme: CSSProperties = { ...chip, cursor: "pointer", font: "inherit", fontSize: chip.fontSize };
const cipIzabran: CSSProperties = {
  ...cipDugme,
  background: "var(--brand-primary)",
  borderColor: "var(--brand-primary)",
  color: "var(--on-primary)",
};
